import {
  Box,
  Button,
  Card,
  CardContent,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Tooltip,
  Typography,
  useTheme
} from '@mui/material';
import { useState } from 'react';
import Grid from '@mui/material/Grid2';
import { useForm } from 'react-hook-form';
import { useWatch } from 'react-hook-form';
import Radio from 'components/Common/Radio';
import Input from 'components/Common/Input';
import { LuRefreshCw } from 'react-icons/lu';
import CopyIcon from 'assets/icons/CopyIcon';
import Spinner from 'components/Common/Spinner';
import { useGetAgent } from 'apis/business/agent/hook';
import PasswordInput from 'components/Common/PasswordInput';
import CustomProviderIcon from 'assets/icons/CustomProviderIcon';
import JambonzProviderIcon from 'assets/icons/JambonzProviderIcon';
import TwillioProviderIcon from 'assets/icons/TwillioProviderIcon';
import RingCentralProviderIcon from 'assets/icons/RingCentralProviderIcon';
import { useCreateAgentPhoneNumber, useDeleteAgentPhoneNumber } from 'apis/business/agent/deployPhone/hook';
// import { Link } from 'react-router-dom';
// import { FaRegQuestionCircle } from 'react-icons/fa';
// import { FaExternalLinkAlt } from 'react-icons/fa';
import TelInput from 'components/Common/TelInput';

export default function ImportNumberForm({ setSelectedOption }: any) {
  const theme = useTheme();
  const [open, setOpen] = useState(false);
  const { data: agent, isLoading } = useGetAgent();
  const { mutateAsync: createAgentPhoneNumber } = useCreateAgentPhoneNumber();
  const { mutateAsync: deleteAgentPhoneNumber } = useDeleteAgentPhoneNumber();

  const { control, handleSubmit } = useForm({
    defaultValues: {
      phone_number: {
        provider: agent?.active_number?.provider || 'twilio',
        imported: true,
        phone_number: '',
        sip_config: {
          sip_uri: '',
          username: '',
          password: '',
          // outbound_proxy: '',
          // auth_username: '',
          pstn_trunk_name: '',
          kickcall_trunk_name: '',
          override_from: ''
        }
      }
    },
    values: {
      phone_number: {
        ...agent?.active_number
      }
    }
  });
  const selectedProvider = useWatch({
    control,
    name: 'phone_number.provider'
  });
  const isDisabled = Boolean(agent?.active_number);
  const isCustom = selectedProvider === 'custom';

  const onSubmit = async (data: any) => {
    data.phone_number.imported = true;
    createAgentPhoneNumber(data);
  };

  const handleChangeMethod = () => {
    if (agent?.active_number) {
      deleteAgentPhoneNumber();
    } else {
      setSelectedOption(null);
    }
  };

  const handleTooltipClose = () => {
    setOpen(false);
  };

  const handleTooltipOpen = () => {
    setOpen(true);
    handleCopy();
  };

  const handleCopy = async () => {
    const originationUri = process.env.REACT_APP_ORIGINATION_URI;
    await navigator?.clipboard?.writeText(String(originationUri));
  };

  if (isLoading) {
    return <Spinner />;
  }

  return (
    <Box bgcolor={theme.palette.background.paper} padding={3} borderRadius={1}>
      <Box display='flex' justifyContent='space-between' alignItems='center' mb={3}>
        <Box>
          <Typography variant='h5' fontWeight={600}>
            Deploy Phone
          </Typography>
          <Typography variant='body2' color='text.secondary'>
            Choose how you want to deploy your agent to a phone number
          </Typography>
        </Box>
        <Button variant='text' startIcon={<LuRefreshCw />} color='primary' onClick={() => handleChangeMethod()}>
          Change Method
        </Button>
      </Box>
      <Typography variant='h6' fontWeight={600} mb={2}>
        Import Phone Number
      </Typography>
      <Card variant='outlined' sx={{ mb: 3 }}>
        <CardContent>
          <Typography fontWeight={500} mb={1}>
            Provider Selection
          </Typography>
          <Typography variant='body2' color='text.secondary' mb={0}>
            Choose your telephony provider
          </Typography>

          <Grid container spacing={2}>
            <Radio
              name='phone_number.provider'
              control={control}
              label=''
              options={providers.map((option) => ({ ...option, disabled: isDisabled }))}
            />
          </Grid>
          {/* <Link to={getConfigGuideLink(selectedProvider)} style={{ textDecoration: 'none' }}>
            <Box
              sx={{
                display: 'inline-flex',
                alignItems: 'center',
                color: theme.palette.primary.main,
                fontSize: '0.875rem',
                gap: '3px',
                cursor: 'pointer',
                '&:hover': {
                  textDecoration: 'underline'
                }
              }}>
              <FaRegQuestionCircle />
              <Typography variant='body2' component='span'>
                Configuration Guide
              </Typography>
              <Box fontSize={10}>
                <FaExternalLinkAlt />
              </Box>
            </Box>
          </Link> */}
        </CardContent>
      </Card>
      <Card variant='outlined' sx={{ mb: 3 }}>
        <CardContent>
          <Typography fontWeight={500} mb={2}>
            Phone Configuration
          </Typography>

          <Grid container spacing={2}>
            <Grid size={{ xs: 12, md: 6 }}>
              {['twilio', 'ring_central'].includes(selectedProvider) ? (
                <TelInput
                  name='phone_number.phone_number'
                  label='Phone Number'
                  control={control}
                  placeholder='+1416676230'
                  disabled={isDisabled}
                  defaultCountry='US'
                  preferredCountries={['US', 'CA', 'IN']}
                  fullWidth
                  required
                />
              ) : (
                <Input
                  name='phone_number.phone_number'
                  label='Phone Number'
                  placeholder='+1416676230'
                  disabled={isDisabled}
                  control={control}
                  fullWidth
                  required
                />
              )}
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Input
                control={control}
                disabled={isDisabled}
                fullWidth
                label='SIP Domain'
                name='phone_number.sip_config.sip_uri'
                required
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <Input
                control={control}
                disabled={isDisabled}
                fullWidth
                label='Username'
                name='phone_number.sip_config.username'
                required={!isCustom}
              />
            </Grid>
            <Grid size={{ xs: 12, md: 6 }}>
              <PasswordInput
                control={control}
                fullWidth
                disabled={isDisabled}
                label='Password'
                name='phone_number.sip_config.password'
                required={!isCustom}
              />
            </Grid>
            {/* {['custom'].includes(selectedProvider) && (
              <>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Input
                    disabled={isDisabled}
                    control={control}
                    fullWidth
                    label='OutBound Proxy'
                    name='phone_number.sip_config.outbound_proxy'
                  />
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Input
                    control={control}
                    disabled={isDisabled}
                    fullWidth
                    label='Auth Username'
                    name='phone_number.sip_config.auth_username'
                  />
                </Grid>
              </>
            )} */}
            {selectedProvider === 'jambonz' && (
              <>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Input
                    disabled={isDisabled}
                    control={control}
                    fullWidth
                    label='PSTN Trunk Name'
                    placeholder='The name of the Carrier you configured in jambonz'
                    name='phone_number.sip_config.pstn_trunk_name'
                    required
                  />
                  <Typography fontSize='12px' color='secondary' lineHeight={1.2} marginTop={0.8}>
                    The name of the Carrier you configured in jambonz towards your SIP service provider
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Input
                    control={control}
                    disabled={isDisabled}
                    fullWidth
                    label='Kickcall Trunk Name'
                    placeholder='The name of the Carrier you configured for Kickcall'
                    name='phone_number.sip_config.kickcall_trunk_name'
                    required
                  />
                  <Typography fontSize='12px' color='secondary'>
                    The name of the Carrier you configured in jambonz for Kickcall
                  </Typography>
                </Grid>
                <Grid size={{ xs: 12, md: 6 }}>
                  <Input
                    control={control}
                    disabled={isDisabled}
                    fullWidth
                    label='Override From (Optional)'
                    placeholder='e.g. 401'
                    name='phone_number.sip_config.override_from'
                  />
                  <Typography fontSize='12px' color='secondary'>
                    Override from in your SIP trunk when using extension outbound, by default phone number will be used
                  </Typography>
                </Grid>
              </>
            )}
            <Grid size={{ xs: selectedProvider === 'jambonz' ? 6 : 12, md: selectedProvider === 'jambonz' ? 6 : 12 }}>
              <Input
                value={originationUri}
                focused
                control={control}
                fullWidth
                disabled
                label='Origination URI'
                name='origination_uri'
                InputProps={{
                  endAdornment: (
                    <InputAdornment position='end'>
                      <ClickAwayListener onClickAway={handleTooltipClose}>
                        <Tooltip
                          PopperProps={{
                            disablePortal: true
                          }}
                          onClose={handleTooltipClose}
                          open={open}
                          disableFocusListener
                          disableHoverListener
                          disableTouchListener
                          placement='top'
                          title='Copied'
                        >
                          <IconButton onClick={handleTooltipOpen}>
                            <CopyIcon />
                          </IconButton>
                        </Tooltip>
                      </ClickAwayListener>
                    </InputAdornment>
                  )
                }}
              />
              <Typography fontSize='12px' color='secondary'>
                Use this URI to configure your SIP endpoint.
              </Typography>
            </Grid>
          </Grid>
        </CardContent>
      </Card>

      {!isDisabled && (
        <Button variant='contained' color='primary' onClick={handleSubmit(onSubmit)} sx={{ textTransform: 'none' }}>
          Save Configuration
        </Button>
      )}
    </Box>
  );
}

// const getConfigGuideLink = (selectedProvider: string) => {
//   switch (selectedProvider) {
//     case 'twilio':
//       return 'https://app.gitbook.com/o/YFiMZIrNBsJVMxpWDJBL/s/fbG6GnnTaKYqci9JvtU8/~/changes/52/telephony/voice-provider/how-to-sip-with-twilio';
//     case 'ring_central':
//       return 'https://app.gitbook.com/o/YFiMZIrNBsJVMxpWDJBL/s/fbG6GnnTaKYqci9JvtU8/~/changes/52/telephony/voice-provider/how-to-sip-with-ring-central';
//     case 'jambonz':
//       return 'https://app.gitbook.com/o/YFiMZIrNBsJVMxpWDJBL/s/fbG6GnnTaKYqci9JvtU8/~/changes/52/telephony/voice-provider/how-to-sip-with-jambonz';
//     default:
//       return 'https://app.gitbook.com/o/YFiMZIrNBsJVMxpWDJBL/s/fbG6GnnTaKYqci9JvtU8/~/changes/53/telephony/voice-provider';
//   }
// };

const providers = [
  {
    value: 'twilio',
    label: (
      <Box border='1px solid #ECECEC' px={1} py={0.5} borderRadius={1} display='flex' gap={1} alignItems='center'>
        <Box height='20px' width='20px' display='flex' alignItems='center'>
          <TwillioProviderIcon />
        </Box>
        Twillio
      </Box>
    )
  },
  {
    value: 'ring_central',
    label: (
      <Box border='1px solid #ECECEC' px={1} py={0.5} borderRadius={1} display='flex' gap={1} alignItems='center'>
        <Box height='20px' width='20px' display='flex' alignItems='center'>
          <RingCentralProviderIcon />
        </Box>
        Ring Central
      </Box>
    )
  },
  {
    value: 'jambonz',
    label: (
      <Box border='1px solid #ECECEC' px={1} py={0.5} borderRadius={1} display='flex' gap={1} alignItems='center'>
        <Box height='20px' width='20px' display='flex' alignItems='center'>
          <JambonzProviderIcon />
        </Box>
        Jambonz
      </Box>
    )
  },
  {
    value: 'custom',
    label: (
      <Box border='1px solid #ECECEC' px={1} py={0.5} borderRadius={1} display='flex' gap={1} alignItems='center'>
        <Box height='20px' width='20px' display='flex' alignItems='center'>
          <CustomProviderIcon />
        </Box>
        Custom
      </Box>
    )
  }
];
const originationUri = process.env.REACT_APP_ORIGINATION_URI;
